import * as THREE from 'three';

/**
 * The sky: a photo (an equirectangular panorama) on a dome that travels with the
 * camera. Two of them — a clear day and an overcast one — crossfaded by the
 * weather and tinted for dusk and night. The colour at the photo's horizon is
 * sampled when it loads, and the weather uses it as the fog colour, so the land
 * fades into exactly the colour the sky has where they meet: that's what makes
 * the edge of the ground disappear.
 */
const R = 380;

type DomeMesh = THREE.Mesh<THREE.SphereGeometry, THREE.MeshBasicMaterial>;

export class SkyDome {
  readonly root = new THREE.Group();
  /** the clear sky's colour at the horizon (sampled from the photo when it loads) */
  readonly horizon = new THREE.Color(0xdfe7ee);
  readonly horizonOvercast = new THREE.Color(0xd3d9de);
  private day: DomeMesh;
  private overcast: DomeMesh;

  constructor(scene: THREE.Object3D, dayUrl: string, overcastUrl: string) {
    const geo = new THREE.SphereGeometry(R, 48, 24);
    geo.scale(-1, 1, 1); // inside out, so the picture reads the right way round from the middle
    const make = (order: number): DomeMesh => {
      const m = new THREE.Mesh(geo, new THREE.MeshBasicMaterial({ fog: false, depthWrite: false, transparent: true, opacity: 0 }));
      m.renderOrder = order;
      m.frustumCulled = false;
      m.visible = false;
      return m;
    };
    this.day = make(-2);
    this.overcast = make(-1);
    this.root.add(this.day, this.overcast);
    scene.add(this.root);
    this.load(dayUrl, this.day, this.horizon);
    this.load(overcastUrl, this.overcast, this.horizonOvercast);
  }

  private load(url: string, mesh: DomeMesh, horizon: THREE.Color): void {
    new THREE.TextureLoader().load(url, (tex) => {
      tex.colorSpace = THREE.SRGBColorSpace;
      mesh.material.map = tex;
      mesh.material.needsUpdate = true;
      mesh.visible = true;
      // the horizon band, averaged into one pixel
      try {
        const img = tex.image as HTMLImageElement;
        const c = document.createElement('canvas');
        c.width = 1;
        c.height = 1;
        const g = c.getContext('2d')!;
        g.drawImage(img, 0, img.height * 0.485, img.width, img.height * 0.03, 0, 0, 1, 1);
        const [r, gg, b] = g.getImageData(0, 0, 1, 1).data;
        horizon.setRGB(r / 255, gg / 255, b / 255, THREE.SRGBColorSpace);
      } catch {
        /* keep the default */
      }
    });
  }

  /** `overcast` 0 (clear) .. 1 (the overcast photo on top); `tint` white by day, dark blue at night */
  set(overcast: number, tint: THREE.Color): void {
    this.day.material.opacity = 1;
    this.overcast.material.opacity = overcast;
    this.day.material.color.copy(tint);
    this.overcast.material.color.copy(tint);
  }

  /** the dome rides with the camera so you can never walk to its edge */
  follow(cameraPos: THREE.Vector3): void {
    this.root.position.copy(cameraPos);
  }
}
